import { cityByAirport, distinguishingAirportName } from '@voll-intelligence/domain';
import { airportLabel, type AirportSwitch } from '@voll-intelligence/genui-contract';
import type {
  FlightAirportSwitchView,
  FlightOfferCopy,
  FlightOfferPlaces,
  FlightPolicyCopy,
} from '@voll-intelligence/intelligence-ui-kit';
import { FLIGHT_POLICY_RULE_KEYS } from './flight_policy_rule_keys.js';
import { flightLegCopy } from './flight_leg_copy.js';
import { genUiI18n } from './i18n.js';

/** Os textos de uma oferta de voo do kit no locale do host. Função pura, como o
 *  `flightFilterCopy`: o host memoiza por locale. */
export function flightOfferCopy(locale: string | null | undefined): FlightOfferCopy {
  const { t } = genUiI18n(locale);
  return {
    leg: flightLegCopy(locale),
    legSingle: t('flightOffers.legSingle'),
    seats: t('flightOffers.seats'),
    chosen: t('flightOffers.chosen'),
    pinOff: t('flight.pin.off'),
    pinOn: t('flight.pin.on'),
    outPolicy: t('flight.policyDetails.outPolicy'),
    outPolicyAria: t('flight.row.outPolicyAria'),
    badge: {
      best: t('flight.badge.best'),
      cheap: t('flight.badge.cheap'),
      fast: t('flight.badge.fast'),
    },
    baseRoundTrip: t('flightOffers.baseRoundTrip'),
    baseOneWay: t('flightOffers.baseOneWay'),
    fromPrice: t('flightOffers.fromPrice'),
    viewFares: t('flightOffers.viewFares'),
    closeFares: t('flightOffers.closeFares'),
    slide: (position, total) => t('flightOffers.slideAria', { position, total }),
    selectOffer: t('flightFilter.selectOffer'),
    selectOfferAria: (position) => t('flightFilter.selectOfferAria', { position }),
    airportSwitch: {
      row: (arrival, departure) => t('flightOffers.airportSwitch.row', { arrival, departure }),
      rowExpected: (arrival, departure) =>
        t('flightOffers.airportSwitch.rowExpected', { arrival, departure }),
    },
  };
}

/** Os textos da gaveta de política do kit no locale do host. */
export function flightPolicyCopy(locale: string | null | undefined): FlightPolicyCopy {
  const { t } = genUiI18n(locale);
  return {
    title: t('flight.policyDetails.title'),
    close: t('common.close'),
    inPolicy: t('flight.policyDetails.inPolicy'),
    outPolicy: t('flight.policyDetails.outPolicy'),
    ruleTitle: (key) => {
      const titleKey = FLIGHT_POLICY_RULE_KEYS[key];
      return titleKey ? t(titleKey) : undefined;
    },
    bookingWindow: (days, scope) =>
      t(
        days === 1
          ? 'flight.policyRule.bookingWindowText_one'
          : 'flight.policyRule.bookingWindowText_other',
        {
          count: days,
          scope: t(
            scope === 'domestic'
              ? 'flight.policyRule.scope.domestic'
              : 'flight.policyRule.scope.international',
          ),
        },
      ),
  };
}

/** O cadastro de aeroportos do domain, na forma que a oferta do kit lê. */
export const FLIGHT_OFFER_PLACES: FlightOfferPlaces = {
  cityOf: cityByAirport,
  airportOf: distinguishingAirportName,
};

/** O desvio de aeroporto da volta (#4203), nomeado pelo cadastro do contrato. */
export function airportSwitchView(
  found: AirportSwitch | null | undefined,
): FlightAirportSwitchView | null {
  if (!found) return null;
  return {
    expected: found.expected,
    arrival: airportLabel(found.outboundArrival),
    departure: airportLabel(found.returnDeparture),
  };
}
